import axios from "axios";

// Use relative `/api` so the frontend always calls the SAME origin that serves
// the page. This works on:
//   • the Emergent preview URL
//   • the production deployment URL
//   • any custom domain (e.g. axelcorporation.com)
// without relying on a build-time REACT_APP_BACKEND_URL value.
const API_BASE = "/api";

const TOKEN_STORAGE_KEY = "magazzino_access_token";

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* localStorage unavailable */
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token to every request when available. This is the primary
// auth mechanism in production behind HTTPS edge networks where cookies can
// be unreliable. The backend also accepts the same JWT via the
// `access_token` cookie as a fallback (preview/local dev).
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, force redirect to /login (session expired or not logged in).
// Skip the redirect for the /auth/me probe so AuthContext can detect logout silently.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.includes("/auth/")) {
      clearAuthToken();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE };

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Errore sconosciuto";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}
